import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Calendar, Phone, EnvelopeSimple, ArrowLeft, Trash, Tag, User, PencilSimple, Heart, ShareNetwork } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { getCategoryLabel } from '../lib/categories';
import { ShareButtons } from '../components/ShareButtons';
import { ReviewSection } from '../components/ReviewSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const saleIcon = L.divIcon({
  className: 'custom-marker',
  html: `<div style="width:40px;height:40px;background:#FF6B35;border:3px solid #004E89;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;box-shadow:3px 3px 0 #004E89"><div style="width:14px;height:14px;background:white;border-radius:50%;transform:rotate(45deg)"></div></div>`,
  iconSize: [40, 40],
  iconAnchor: [20, 40],
});

export default function SaleDetail() {
  const { saleId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePhoto, setActivePhoto] = useState(0);
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    const fetchSale = async () => {
      try {
        const { data } = await axios.get(`${API}/sales/${saleId}`);
        setSale(data);
      } catch {
        toast.error('Sale not found');
        navigate('/explore');
      } finally {
        setLoading(false);
      }
    };
    fetchSale();
  }, [saleId, navigate]);

  useEffect(() => {
    if (user && saleId) {
      axios.get(`${API}/favorites/ids`, { withCredentials: true })
        .then(({ data }) => setIsSaved(data.sale_ids?.includes(saleId)))
        .catch(() => {});
    }
  }, [user, saleId]);

  const toggleSave = async () => {
    if (!user) { navigate('/login'); return; }
    try {
      if (isSaved) {
        await axios.delete(`${API}/favorites/${saleId}`, { withCredentials: true });
        setIsSaved(false);
        toast.success('Removed from saved');
      } else {
        await axios.post(`${API}/favorites/${saleId}`, {}, { withCredentials: true });
        setIsSaved(true);
        toast.success('Saved to favorites!');
      }
    } catch {
      toast.error('Failed to update favorites');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this listing?')) return;
    try {
      await axios.delete(`${API}/sales/${saleId}`, { withCredentials: true });
      toast.success('Sale deleted');
      navigate('/my-listings');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[calc(100vh-64px)]" data-testid="sale-detail-loading">
      <div className="text-xl font-bold text-[#004E89]">Loading...</div>
    </div>
  );

  if (!sale) return null;

  const isOwner = user && (user._id === sale.created_by || user.role === 'admin');
  const placeholderImg = 'https://images.pexels.com/photos/4923440/pexels-photo-4923440.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#F9F9F6] px-4 py-6" data-testid="sale-detail-page">
      <div className="max-w-5xl mx-auto">
        {/* Back */}
        <Link to="/explore" className="inline-flex items-center gap-2 text-[#5C7A99] hover:text-[#004E89] font-bold text-sm mb-6 transition-colors" data-testid="back-to-explore">
          <ArrowLeft size={18} weight="bold" />
          Back to Explore
        </Link>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Left: Photos + Details */}
          <div className="lg:col-span-3 space-y-6">
            {/* Photo Gallery */}
            <div className="bg-white border-2 border-[#004E89] rounded-2xl overflow-hidden shadow-[4px_4px_0px_#004E89]" data-testid="photo-gallery">
              <div className="h-[300px] sm:h-[400px]">
                <img
                  src={sale.photos?.length > 0 ? `${API}/files/${sale.photos[activePhoto]}` : placeholderImg}
                  alt={sale.title}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.src = placeholderImg; }}
                />
              </div>
              {sale.photos?.length > 1 && (
                <div className="photo-gallery-scroll p-3">
                  {sale.photos.map((photo, idx) => (
                    <button key={idx} onClick={() => setActivePhoto(idx)}
                      className={`w-16 h-16 rounded-lg overflow-hidden border-2 flex-shrink-0 transition-all ${idx === activePhoto ? 'border-[#FF6B35] ring-2 ring-[#FF6B35]' : 'border-[#004E89]'}`}
                      data-testid={`photo-thumb-${idx}`}
                    >
                      <img src={`${API}/files/${photo}`} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.src = placeholderImg; }} />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Description */}
            <div className="bg-white border-2 border-[#004E89] rounded-2xl p-6 shadow-[4px_4px_0px_#004E89]">
              <h2 className="text-xl font-bold text-[#004E89] mb-4" style={{ fontFamily: 'Outfit' }}>Description</h2>
              <p className="text-[#004E89] leading-relaxed whitespace-pre-wrap" data-testid="sale-description">{sale.description}</p>

              {sale.items_categories?.length > 0 && (
                <div className="mt-4 pt-4 border-t-2 border-[#F0F0F0]">
                  <h3 className="text-sm font-bold text-[#5C7A99] uppercase tracking-wide mb-2">Item Categories</h3>
                  <div className="flex flex-wrap gap-2" data-testid="item-categories">
                    {sale.items_categories.map(cat => (
                      <span key={cat} className="tag bg-[#F9F9F6] text-[#004E89] border-[#004E89]">{cat}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Videos */}
            {sale.videos?.length > 0 && (
              <div className="bg-white border-2 border-[#004E89] rounded-2xl p-6 shadow-[4px_4px_0px_#004E89]" data-testid="video-gallery">
                <h2 className="text-xl font-bold text-[#004E89] mb-4" style={{ fontFamily: 'Outfit' }}>Videos</h2>
                <div className="space-y-3">
                  {sale.videos.map((video, idx) => {
                    const ext = video.split('.').pop()?.toLowerCase();
                    const mimeMap = { mp4: 'video/mp4', webm: 'video/webm', mov: 'video/quicktime', ogg: 'video/ogg', avi: 'video/x-msvideo' };
                    const mimeType = mimeMap[ext] || 'video/mp4';
                    return (
                      <div key={idx} className="rounded-xl overflow-hidden border-2 border-[#004E89]">
                        <video controls playsInline preload="metadata" className="w-full max-h-[300px] bg-black" data-testid={`video-${idx}`}>
                          <source src={`${API}/files/${video}`} type={mimeType} />
                          <p className="text-white p-4">Your browser does not support this video format. <a href={`${API}/files/${video}`} className="underline text-[#FFD166]">Download the video</a>.</p>
                        </video>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Right: Info + Map */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title + Tags */}
            <div className="bg-white border-2 border-[#004E89] rounded-2xl p-6 shadow-[4px_4px_0px_#004E89]">
              <span className={`tag tag-${sale.category === 'business_closing' ? 'business' : sale.category} mb-3`} data-testid="sale-category-tag">
                {getCategoryLabel(sale.category)}
              </span>
              <h1 className="text-2xl sm:text-3xl font-black text-[#004E89] mb-4" style={{ fontFamily: 'Outfit' }} data-testid="sale-title">
                {sale.title}
              </h1>

              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <MapPin size={20} weight="bold" className="text-[#FF6B35] mt-0.5 flex-shrink-0" />
                  <div>
                    {sale.address_hidden ? (
                      <>
                        <p className="text-sm font-bold text-[#FF6B35]" data-testid="sale-address">Address hidden until sale day</p>
                        <p className="text-xs text-[#5C7A99]">Revealed at 6 AM on {new Date(sale.start_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</p>
                      </>
                    ) : (
                      <>
                        <p className="text-sm font-bold text-[#004E89]" data-testid="sale-address">{sale.address}</p>
                        <p className="text-sm text-[#5C7A99]">{sale.city}, {sale.state} {sale.zip_code}</p>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Calendar size={20} weight="bold" className="text-[#FF6B35] mt-0.5 flex-shrink-0" />
                  <div data-testid="sale-dates">
                    <p className="text-sm font-bold text-[#004E89]">
                      {new Date(sale.start_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                      {sale.start_date !== sale.end_date && ` - ${new Date(sale.end_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`}
                    </p>
                  </div>
                </div>
                {sale.creator_name && (
                  <div className="flex items-center gap-3">
                    <User size={20} weight="bold" className="text-[#FF6B35] flex-shrink-0" />
                    <p className="text-sm text-[#5C7A99]">Posted by <span className="font-bold text-[#004E89]">{sale.creator_name}</span></p>
                  </div>
                )}
              </div>

              {isOwner && (
                <div className="flex gap-2 mt-4">
                  <button onClick={() => navigate(`/edit-sale/${saleId}`)} className="btn-outline py-2 px-4 text-sm flex items-center gap-2" data-testid="edit-sale-button">
                    <PencilSimple size={16} weight="bold" /> Edit Listing
                  </button>
                  <button onClick={handleDelete} className="btn-outline py-2 px-4 text-sm text-red-600 border-red-400 flex items-center gap-2 hover:bg-red-50 hover:text-red-700 shadow-[2px_2px_0px_#dc2626]" data-testid="delete-sale-button">
                    <Trash size={16} weight="bold" /> Delete
                  </button>
                </div>
              )}

              {/* Save/Favorite Button */}
              <button onClick={toggleSave} className={`mt-4 w-full py-2.5 rounded-xl border-2 font-bold text-sm flex items-center justify-center gap-2 transition-all ${isSaved ? 'bg-red-50 border-red-400 text-red-600 shadow-[2px_2px_0px_#dc2626]' : 'border-[#004E89] text-[#004E89] shadow-[2px_2px_0px_#004E89] hover:bg-[#FFD166] hover:bg-opacity-20'}`} data-testid="save-sale-button">
                <Heart size={18} weight={isSaved ? 'fill' : 'bold'} />
                {isSaved ? 'Saved' : 'Save This Sale'}
              </button>

              {/* Share */}
              <div className="mt-4">
                <ShareButtons title={sale.title} url={window.location.href} />
              </div>
            </div>

            {/* Contact */}
            {(sale.contact_name || sale.contact_phone || sale.contact_email) && (
              <div className="bg-white border-2 border-[#004E89] rounded-2xl p-6 shadow-[4px_4px_0px_#004E89]" data-testid="contact-info">
                <h3 className="text-lg font-bold text-[#004E89] mb-3" style={{ fontFamily: 'Outfit' }}>Contact</h3>
                <div className="space-y-2">
                  {sale.contact_name && <p className="text-sm font-medium text-[#004E89]">{sale.contact_name}</p>}
                  {sale.contact_phone && (
                    <a href={`tel:${sale.contact_phone}`} className="flex items-center gap-2 text-sm text-[#5C7A99] hover:text-[#FF6B35]">
                      <Phone size={16} weight="bold" /> {sale.contact_phone}
                    </a>
                  )}
                  {sale.contact_email && (
                    <a href={`mailto:${sale.contact_email}`} className="flex items-center gap-2 text-sm text-[#5C7A99] hover:text-[#FF6B35]">
                      <EnvelopeSimple size={16} weight="bold" /> {sale.contact_email}
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Mini Map */}
            {sale.lat && sale.lng && !sale.address_hidden && (
              <div className="bg-white border-2 border-[#004E89] rounded-2xl overflow-hidden shadow-[4px_4px_0px_#004E89] h-[200px]" data-testid="sale-mini-map">
                <MapContainer center={[sale.lat, sale.lng]} zoom={14} style={{ width: '100%', height: '100%' }} zoomControl={false} scrollWheelZoom={false}>
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <Marker position={[sale.lat, sale.lng]} icon={saleIcon}>
                    <Popup>{sale.title}</Popup>
                  </Marker>
                </MapContainer>
              </div>
            )}
          </div>
        </div>

        {/* Reviews - Full Width */}
        <div className="mt-6">
          <ReviewSection saleId={saleId} />
        </div>
      </div>
    </div>
  );
}
